/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de RECIBOS (`/recibos`).
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`, do
 * `serviceOrdersListFilters` e do `clientsListFilters`: são regras que decidem
 * QUAIS comprovantes o prestador vê e em que ordem, e todas erram em silêncio —
 * uma lista recortada errado não dá erro, só esconde documento.
 *
 * ─── O QUE É ESPECÍFICO DESTA LISTA ─────────────────────────────────────────
 *
 * · **`receipts` NÃO TEM COLUNA `status` (#57).** O estado é DERIVADO: com
 *   `cancelled_at` preenchido o recibo está cancelado, senão está emitido. As
 *   linhas chegam do cache tipadas como `any`, então `r.status === 'cancelled'`
 *   COMPILA e devolve `false` para sempre — um recibo cancelado passaria por
 *   válido, que é o pior erro possível num comprovante de pagamento. Por isso o
 *   predicado é UM só (`receiptIsCancelled`) e a página inteira o consome: o
 *   selo do card, a faixa de botões e a soma do hero não podem discordar sobre
 *   o que está cancelado.
 *
 * · **A DATA DO RECIBO É `issue_date`, e ela é DATE (date-only).** É a data de
 *   emissão que o documento imprime e que tem valor probatório — não
 *   `created_at`, que é quando alguém digitou. `new Date('2026-07-25')` é
 *   meia-noite UTC e em UTC-3 cai no DIA ANTERIOR (#114/#54), então a leitura
 *   passa por `localDateFromIsoDatePart`, o mesmo helper que a Agenda usa para
 *   o mesmo tipo de coluna. Recibo sem `issue_date` cai em `created_at` — o
 *   salvo offline pode chegar assim, e sumi-lo da ordenação por causa disso
 *   seria escondê-lo justamente de quem está fechando o mês no campo.
 *
 * · **A busca por `#N` é EXATA e EXCLUSIVA**, igual à de `/quotes` (#190c) e à
 *   de `/ordens-servico`: `sequence_id` é inteiro exibido com 4 casas, então
 *   casar como texto faria "#7" trazer #0017 e #0070. O parser é o MESMO
 *   (`parseSequenceQuery`) — duas cópias divergiriam na primeira correção.
 *
 * O parser de dinheiro é o `quoteTotalNumber` (#123a) — `amount` chega como
 * `number` do formulário e como STRING do PostgREST (`numeric`) — e a
 * normalização de texto dobra caixa E acento (#190d), senão quem digita
 * "conceicao" no teclado do celular recebe zero por "Conceição".
 */
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { normalizeSearchText, parseSequenceQuery } from '@/lib/quoteSearch';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

/**
 * O que este módulo enxerga de um recibo.
 *
 * `status` NÃO está aqui de propósito: a coluna não existe (#57), e declará-la
 * daria ao leitor a impressão de que dá para consultá-la.
 */
export interface ReceiptRow {
  id?: string | null;
  payer_name?: string | null;
  description?: string | null;
  sequence_id?: number | null;
  amount?: unknown;
  issue_date?: string | null;
  created_at?: string | null;
  cancelled_at?: string | null;
}

// ─── Estado derivado (#57) ──────────────────────────────────────────────────

/**
 * A FONTE ÚNICA do "este recibo foi cancelado".
 *
 * Qualquer coisa preenchida em `cancelled_at` cancela; `null`/ausente não. Uma
 * string vazia (que já apareceu vinda de cache antigo) NÃO cancela — `Boolean('')`
 * é `false`, e é o resultado certo: sem carimbo não houve cancelamento.
 */
export function receiptIsCancelled(receipt: ReceiptRow): boolean {
  return Boolean(receipt?.cancelled_at);
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: número, pagador e descrição — exatamente o que o card
 * mostra. Buscar em campo que a linha não exibe devolveria resultados que o
 * usuário não consegue explicar.
 */
export function receiptMatchesSearch(receipt: ReceiptRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  const wantedSequence = parseSequenceQuery(term);
  if (wantedSequence !== null && receipt?.sequence_id === wantedSequence) return true;
  // O `#` explícito NÃO cai no texto: quem pergunta por número quer o documento
  // daquele número, e devolver os dois misturados é o que faz a busca parecer
  // quebrada (#190c).
  if (term.trim().startsWith('#')) return false;

  if (normalizeSearchText(receipt?.payer_name).includes(needle)) return true;
  if (normalizeSearchText(receipt?.description).includes(needle)) return true;
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type ReceiptSortOption = 'recent' | 'oldest' | 'highest' | 'lowest' | 'payer';

export const RECEIPT_SORT_LABELS: Record<ReceiptSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  payer: 'Nome do pagador',
};

export const RECEIPT_SORT_ORDER: readonly ReceiptSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'payer',
];

/**
 * `recent` é o PADRÃO porque é como a lista sempre se comportou (a consulta já
 * devolve `issue_date desc`). Trocar o padrão junto com o redesenho mudaria a
 * tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_RECEIPT_SORT: ReceiptSortOption = 'recent';

/**
 * O instante do recibo, pelo calendário LOCAL.
 *
 * `issue_date` (DATE) manda; sem ela, `created_at` (TIMESTAMP). Data ilegível
 * vai para o FIM em "mais recentes" — recibo sem data é exceção, e exceção não
 * ocupa a primeira linha da lista.
 */
export function receiptTime(receipt: ReceiptRow): number {
  if (receipt?.issue_date) {
    const parsed = localDateFromIsoDatePart(String(receipt.issue_date)).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  const created = new Date(receipt?.created_at ?? '').getTime();
  return Number.isFinite(created) ? created : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem pagador" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function amountOf(receipt: ReceiptRow): number {
  return quoteTotalNumber(receipt?.amount);
}

/** Não muta a entrada: a lista da tela é derivada da consulta. */
export function sortReceiptsBy<T extends ReceiptRow>(
  receipts: readonly T[],
  option: ReceiptSortOption,
): T[] {
  const rows = [...receipts];
  // Desempate ESTÁVEL por id (#93): sem ele, dois recibos do mesmo dia (e o
  // mesmo dia é a regra, não a exceção — vários recibos saem numa sentada)
  // trocam de lugar entre renders e a lista "pisca" sem ninguém ter mexido.
  const byId = (a: T, b: T) => String(a?.id).localeCompare(String(b?.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => receiptTime(a) - receiptTime(b) || byId(a, b));
    case 'highest':
      return rows.sort((a, b) => amountOf(b) - amountOf(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => amountOf(a) - amountOf(b) || byId(a, b));
    case 'payer':
      return rows.sort(
        (a, b) => textOf(a?.payer_name).localeCompare(textOf(b?.payer_name)) || byId(a, b),
      );
    case 'recent':
    default:
      return rows.sort((a, b) => receiptTime(b) - receiptTime(a) || byId(a, b));
  }
}

// ─── Recorte por ESTADO (os botões da faixa) ────────────────────────────────

export type ReceiptStatusFilter = 'all' | 'issued' | 'cancelled';

/**
 * Os botões da faixa. São TRÊS — e não cinco como em `/quotes` — porque o
 * recibo não tem máquina de estados: ele nasce emitido e só pode ser cancelado.
 * Inventar recortes intermediários seria oferecer um botão de recorte
 * garantidamente vazio (#91).
 */
export const RECEIPT_STATUS_FILTER_OPTIONS: readonly {
  value: ReceiptStatusFilter;
  label: string;
}[] = [
  { value: 'all', label: 'Todos' },
  { value: 'issued', label: 'Emitidos' },
  { value: 'cancelled', label: 'Cancelados' },
];

export function matchesReceiptStatus(receipt: ReceiptRow, filter: ReceiptStatusFilter): boolean {
  if (filter === 'all') return true;
  return receiptIsCancelled(receipt) === (filter === 'cancelled');
}

/**
 * Quantos recibos cada botão traria — contados sobre o recorte JÁ filtrado por
 * busca, período e valor, mas ANTES do estado.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 com uma busca ativa que devolve 3, e contar depois do estado
 * zeraria todos os outros botões (#193/#196).
 */
export function countReceiptsByStatus(
  receipts: readonly ReceiptRow[],
): Record<ReceiptStatusFilter, number> {
  const counts: Record<ReceiptStatusFilter, number> = {
    all: receipts.length,
    issued: 0,
    cancelled: 0,
  };
  for (const receipt of receipts) {
    if (receiptIsCancelled(receipt)) counts.cancelled += 1;
    else counts.issued += 1;
  }
  return counts;
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/**
 * Presets oferecidos no modal. Subconjunto EXPLÍCITO dos de `reportPeriod`, o
 * mesmo de `/quotes` e `/ordens-servico`: os longos de relatório não ajudam a
 * achar um recibo, e `custom` exigiria um calendário dentro do modal.
 */
export const RECEIPT_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

/**
 * `range` nulo = sem filtro de período. Recibo SEM data legível NÃO é
 * recortado: sumir com ele por causa de um carimbo estranho é pior do que
 * mostrá-lo fora da faixa pedida — o usuário não teria como saber que ele
 * existe (#193-d).
 */
export function matchesReceiptPeriod(
  receipt: ReceiptRow,
  range: ReportDateRange | null,
): boolean {
  if (!range) return true;
  const time = receiptTime(receipt);
  if (!time) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesReceiptValue(receipt: ReceiptRow, range: ListValueRange): boolean {
  return matchesListRange(receipt?.amount, range);
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O estado fica de fora: ele tem os botões visíveis logo acima, então já se
 * anuncia sozinho. Contá-lo faria o selo dizer "1" com o modal em branco.
 */
export function countActiveReceiptFilters(
  period: ReportPeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** `true` quando o recorte É exatamente o estado morto (ver `includeDead`). */
export function isDeadReceiptStatusFilter(filter: ReceiptStatusFilter): boolean {
  return filter === 'cancelled';
}

export interface ReceiptListSummary {
  /** Recibos no recorte — todos, inclusive os cancelados. */
  count: number;
  /** Soma dos que ainda comprovam pagamento (fora os cancelados). */
  total: number;
  /** Quantos ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeReceiptsOptions {
  /**
   * Soma TAMBÉM os cancelados.
   *
   * Existe por causa do filtro explícito por "Cancelados": excluir todo mundo
   * ali faria o hero anunciar R$ 0,00 com a lista cheia embaixo — o número mais
   * visível da tela contradizendo o que ela mostra. A exclusão serve para a
   * faixa "Todos" não INFLAR o que foi comprovadamente recebido; quando o
   * usuário pede justamente os cancelados, a pergunta mudou.
   */
  includeDead?: boolean;
}

/** Resumo do recorte JÁ FILTRADO (busca, estado, período e valor aplicados). */
export function summarizeReceiptList(
  receipts: readonly ReceiptRow[],
  options: SummarizeReceiptsOptions = {},
): ReceiptListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const receipt of receipts) {
    if (!options.includeDead && receiptIsCancelled(receipt)) {
      excludedCount += 1;
      continue;
    }
    total += amountOf(receipt);
  }

  return { count: receipts.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto.
 *
 * A soma sai do recorte já filtrado (busca + estado + período + valor), então
 * ela muda enquanto o usuário digita. Sem dizer de qual recorte ela saiu,
 * "R$ 12.400,00" sob o título "Recibos" lê como "é isso que eu recebi" quando
 * pode ser o recorte de cancelados com uma busca ativa (#182/#188).
 */
export function receiptListTotalLabel(filter: ReceiptStatusFilter): string {
  switch (filter) {
    case 'issued':
      return 'Total emitido';
    // O estado morto ganha rótulo PRÓPRIO porque, sob o filtro dele, a soma
    // passa a incluí-lo (`includeDead`): dizer "Total em recibos" ali faria o
    // número parecer dinheiro recebido quando ele é justamente o oposto.
    case 'cancelled':
      return 'Total cancelado';
    default:
      return 'Total em recibos';
  }
}
